import * as React from 'react';
import styles from './notification.module.scss';
import classNames from 'classnames';

/**
 * Shared notifications for easy access.
 */
let globalInstance: Notification[] = []

interface Props {
  /**
   * Whether to make this notification accessible from anywhere.
   */
  global?: boolean;
}

class NotificationDisplay {
  message: React.ReactChild = '';
}

class State {
  queue: NotificationDisplay[] = [];
  current: NotificationDisplay;
  visible: boolean = false;
}

export type HideNotificationCallback = () => void;

/**
 * Displays a small, important message.
 * @author Johan Svensson
 */
export default class Notification extends React.Component<Props, State> {
  state = new State();
  shownAt: Date;
  minShowTimeInSeconds = 2.15;

  /**
   * @returns All shared and mounted notification instances.
   */
  static get global() {
    return globalInstance;
  }

  /**
   * Shows a message with the first shared component, if one exists.
   * @param holdWithCloseCallback if present, keep the notification open until the callback is invoked
   */
  static show(
    message: React.ReactChild,
    hold = false
  ): HideNotificationCallback {
    let instance = globalInstance[0];
    if (instance) {
      return instance.show(message, 3500, hold);
    }
  }

  animationDuration = 250;
  hideTimeoutId: any = -1;

  /**
   * @returns Time since `show` was invoked, in seconds.
   */
  get showTime() {
    if (!this.shownAt) {
      return 0;
    }
    return (Date.now() - this.shownAt.getTime()) / 1000;
  }

  componentDidMount() {
    if (this.props.global) {
      globalInstance.push(this);
    }
  }

  componentWillUnmount() {
    globalInstance = globalInstance.filter(n => n != this);
  }

  /**
   * Shows a notification message.
   */
  show(
    message: React.ReactChild,
    duration: number = 3500,
    hold = false
  ): HideNotificationCallback {

    if (this.shownAt && this.showTime < this.minShowTimeInSeconds) {
      //  Wait for previous message
      setTimeout(() => this.show(message, duration, hold), this.minShowTimeInSeconds - this.showTime);
      return;
    }

    const display: NotificationDisplay = { message };

    let { state } = this;
    if (state.visible) {
      this.setState(s => ({
        queue: [...s.queue, display]
      }));
      return;
    }

    this.setState({
      current: display,
      visible: true
    });

    this.shownAt = new Date();

    if (!hold) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = setTimeout(() => this.hide(), duration);
    } else {
      return () => {
        this.hide()
      }
    }
  }

  hide() {
    this.setState({
      visible: false
    });
  }

  onHidden() {
    const { state } = this;
    const queue = [...state.queue];
    if (queue?.length) {
      const next = queue.splice(0, 1)[0];

      this.show(next.message);

      this.setState({ queue });
    }
  }

  render() {
    let { state, props } = this;

    return (
      <div className={classNames({
        [styles.notification]: true,
        [styles.visible]: state.visible
      })}
        style={{ transitionDuration: `${this.animationDuration}ms` }}
        onTransitionEnd={() => {
          if (!state.visible) {
            this.onHidden();
          }
        }}>
        <p className={styles.text}>{state.current?.message}</p>
      </div>
    )
  }
}
